import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Users, CalendarCheck, Wallet } from "lucide-react";
import Seo from "@/seo/Seo";
import { SeoLayout, Prose, FaqBlock } from "@/components/site/SeoLayout";
import { CITIES, SERVICES, SERVICE_ORDER } from "@/seo/content";
import { localBusinessLd, faqLd, breadcrumbLd } from "@/seo/schema";

const WHY = [
  { icon: ShieldCheck, t: "Eigen, gescreende schoonmakers" },
  { icon: Users, t: "Vaste schoonmaker waar mogelijk" },
  { icon: CalendarCheck, t: "Flexibel in te plannen" },
  { icon: Wallet, t: "Transparante tarieven, geen verborgen kosten" },
];

const STEPS = [
  "Vraag online of via WhatsApp een schoonmaker aan.",
  "Wij matchen u met een gescreende schoonmaker in de buurt.",
  "De schoonmaker komt op een moment dat u uitkomt.",
  "U geniet van een schoon resultaat, keer op keer.",
];

export default function CityPage({ slug }) {
  const c = CITIES[slug];
  const path = `/${slug}`;

  const breadcrumb = [
    { name: "Home", path: "/" },
    { name: `Schoonmaker ${c.city}`, path },
  ];

  const faqs = [
    { q: `Werken jullie in heel ${c.city}?`, a: `Ja, MyCleaner is actief in heel ${c.city} en omgeving. Geef bij uw aanvraag uw adres op en wij matchen u met een schoonmaker in de buurt.` },
    { q: "Nemen de schoonmakers materialen mee?", a: "Ja, onze schoonmakers nemen alle benodigde schoonmaakmiddelen mee. U stelt alleen een stofzuiger en dweil beschikbaar." },
    { q: `Hoe snel kan een schoonmaker in ${c.city} beginnen?`, a: "In veel gevallen kunnen we binnen 24 tot 48 uur een geschikte schoonmaker koppelen, afhankelijk van uw wensen en beschikbaarheid." },
  ];

  const sections = [
    { h2: `Waarom kiezen voor MyCleaner in ${c.city}?`, body: c.localBody },
  ];

  return (
    <>
      <Seo
        title={c.seoTitle}
        description={c.metaDesc}
        path={path}
        jsonLd={[localBusinessLd(), faqLd(faqs), breadcrumbLd(breadcrumb)]}
      />
      <SeoLayout breadcrumb={breadcrumb}>
        <span className="inline-flex rounded-full bg-blue-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-blue-700">
          Schoonmaker {c.city}
        </span>
        <h1 className="mt-5 font-heading text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">{c.h1}</h1>

        <Prose intro={c.intro} sections={sections} />

        {/* Services in this city */}
        <section className="mt-12">
          <h2 className="font-heading text-2xl font-semibold text-slate-900">Onze schoonmaakdiensten in {c.city}</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {SERVICE_ORDER.map((o) => (
              <Link
                key={o}
                to={`/${o}`}
                className="group flex items-center justify-between rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <div>
                  <span className="font-heading font-semibold text-slate-900">{SERVICES[o].label}</span>
                  <p className="mt-1 text-sm text-slate-500">in {c.city}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-blue-600 transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </section>

        {/* Why us */}
        <section className="mt-12">
          <h2 className="font-heading text-2xl font-semibold text-slate-900">Voordelen van eigen, gescreende schoonmakers</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {WHY.map((w, i) => {
              const Icon = w.icon;
              return (
                <div key={i} className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3">
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="text-sm font-medium text-slate-700">{w.t}</span>
                </div>
              );
            })}
          </div>
        </section>

        {/* Werkwijze */}
        <section className="mt-12">
          <h2 className="font-heading text-2xl font-semibold text-slate-900">Zo werkt het</h2>
          <div className="mt-6 space-y-3">
            {STEPS.map((step, i) => (
              <div key={i} className="flex items-center gap-4">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 font-heading text-sm font-bold text-white">{i + 1}</span>
                <span className="text-slate-700">{step}</span>
              </div>
            ))}
          </div>
        </section>

        <FaqBlock faqs={faqs} />
      </SeoLayout>
    </>
  );
}
